import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const VerificationForm = ({ formData, onUpdate, onNext, onPrevious, onSubmit }) => {
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const requiredDocuments = [
    {
      id: 'businessLicense',
      title: 'Business License',
      description: 'Valid business license or registration',
      icon: 'FileText',
      required: false
    },
    {
      id: 'insurance',
      title: 'Liability Insurance', 
      description: 'General liability insurance certificate',
      icon: 'Shield',
      required: false
    },
    {
      id: 'taxId',
      title: 'Tax ID/EIN',
      description: 'Federal tax identification number',
      icon: 'Receipt',
      required: false
    },
    {
      id: 'portfolio',
      title: 'Professional Portfolio',
      description: 'Additional work samples or certifications',
      icon: 'Award',
      required: false
    }
  ];

  const subscriptionPlans = [
    {
      id: 'basic',
      name: 'Basic Listing',
      price: 0,
      period: 'month',
      features: [
        'Basic profile listing',
        'Up to 10 portfolio images',
        'Contact information display',
        'Basic search visibility'
      ],
      popular: false
    },
    {
      id: 'premium',
      name: 'Premium Listing',
      price: 29,
      period: 'month',
      features: [
        'Enhanced profile with priority placement',
        'Unlimited portfolio images',
        'Featured in search results',
        'Customer review management',
        'Booking calendar integration',
        'Lead generation tools'
      ],
      popular: true
    },
    {
      id: 'pro',
      name: 'Pro Listing',
      price: 59,
      period: 'month',
      features: [
        'All Premium features',
        'Top placement in search results',
        'Advanced analytics dashboard',
        'Custom branding options',
        'Priority customer support',
        'Marketing campaign tools',
        'Multi-location support'
      ],
      popular: false
    }
  ];

  const handleFileUpload = (documentType, files) => {
    const file = files[0];
    if (file) {
      const documentData = {
        file,
        name: file.name,
        size: file.size,
        type: file.type,
        uploadedAt: new Date().toISOString()
      };
      
      onUpdate({
        documents: {
          ...formData.documents,
          [documentType]: documentData
        }
      });
      
      if (errors[documentType]) {
        setErrors(prev => ({ ...prev, [documentType]: null }));
      }
    }
  };

  const handleInputChange = (field, value) => {
    onUpdate({ [field]: value });
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const removeDocument = (documentType) => {
    const updatedDocuments = { ...formData.documents };
    delete updatedDocuments[documentType];
    onUpdate({ documents: updatedDocuments });
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.selectedPlan) {
      newErrors.selectedPlan = 'Please select a subscription plan';
    }

    if (!formData.termsAccepted) {
      newErrors.termsAccepted = 'You must accept the terms and conditions';
    }

    if (!formData.privacyAccepted) {
      newErrors.privacyAccepted = 'You must accept the privacy policy';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setIsSubmitting(true);
    try {
      await onSubmit();
    } catch (error) {
      console.error('Submission error:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  return (
    <div className="space-y-8">
      {/* Document Upload */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Document Verification
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Upload documents to verify your business and complete your profile. All documents are now optional to accommodate freelancers.
        </p>

        <div className="space-y-6">
          {requiredDocuments.map((doc) => {
            const uploadedDoc = formData.documents?.[doc.id];
            
            return (
              <div key={doc.id} className="border border-border rounded-xl p-6">
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-start space-x-3">
                    <div className="flex items-center justify-center w-10 h-10 bg-primary-50 rounded-lg">
                      <Icon name={doc.icon} size={20} color="#8B4513" />
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-text-primary flex items-center space-x-2">
                        <span>{doc.title}</span>
                        <span className="text-xs text-accent bg-accent-50 px-2 py-1 rounded-full">
                          Optional
                        </span>
                      </h4>
                      <p className="text-sm text-text-secondary font-caption">
                        {doc.description}
                      </p>
                    </div>
                  </div>
                  
                  {uploadedDoc && (
                    <button
                      onClick={() => removeDocument(doc.id)}
                      className="text-error hover:text-error-600 transition-smooth"
                    >
                      <Icon name="Trash2" size={16} color="currentColor" />
                    </button>
                  )}
                </div>

                {uploadedDoc ? (
                  <div className="flex items-center space-x-3 p-3 bg-success-50 rounded-lg">
                    <Icon name="CheckCircle" size={20} color="#228B22" />
                    <div className="flex-1">
                      <p className="font-medium text-success text-sm">{uploadedDoc.name}</p>
                      <p className="text-xs text-success-600 font-caption">
                        {formatFileSize(uploadedDoc.size)} • Uploaded
                      </p>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div className="border-2 border-dashed border-border-light rounded-lg p-6 text-center">
                      <Icon name="Upload" size={24} color="#6B5B4F" className="mx-auto mb-3" />
                      <p className="text-text-primary font-medium mb-2">
                        Drop file here or click to upload
                      </p>
                      <p className="text-sm text-text-secondary font-caption mb-4">
                        PDF, JPG, PNG up to 10MB
                      </p>
                      <input
                        type="file"
                        accept=".pdf,.jpg,.jpeg,.png"
                        onChange={(e) => handleFileUpload(doc.id, e.target.files)}
                        className="hidden"
                        id={`upload-${doc.id}`}
                      />
                      <label htmlFor={`upload-${doc.id}`}>
                        <Button
                          variant="outline"
                          size="sm"
                          iconName="Plus"
                          iconSize={16}
                          className="cursor-pointer"
                        >
                          Choose File
                        </Button>
                      </label>
                    </div>
                    
                    <div className="mt-3 p-3 bg-surface-50 rounded-lg">
                      <p className="text-sm text-text-secondary font-caption flex items-start space-x-2">
                        <Icon name="Info" size={16} color="#6B5B4F" className="mt-0.5 flex-shrink-0" />
                        <span>
                          Don't have one yet? No worries — you can still register and update this later.
                        </span>
                      </p>
                    </div>
                  </div>
                )}
                
                {errors[doc.id] && (
                  <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
                    <Icon name="AlertCircle" size={16} color="currentColor" />
                    <span>{errors[doc.id]}</span>
                  </p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* Subscription Plans */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Choose Your Plan
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Select a subscription plan that fits your business needs
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {subscriptionPlans.map((plan) => (
            <div
              key={plan.id}
              className={`relative border-2 rounded-xl p-6 transition-smooth hover:shadow-soft ${
                formData.selectedPlan === plan.id
                  ? 'border-primary bg-primary-50 shadow-soft'
                  : 'border-border hover:border-primary-200 bg-white'
              } ${plan.popular ? 'ring-2 ring-secondary ring-opacity-50' : ''}`}
            >
              {plan.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                  <span className="bg-secondary text-secondary-foreground px-3 py-1 rounded-full text-xs font-medium">
                    Most Popular
                  </span>
                </div>
              )}
              
              <button
                onClick={() => handleInputChange('selectedPlan', plan.id)}
                className="w-full text-left"
              >
                <div className="mb-4">
                  <h4 className="font-heading font-bold text-lg text-text-primary">
                    {plan.name}
                  </h4>
                  <div className="flex items-baseline space-x-1 mt-2">
                    <span className="text-3xl font-bold text-primary">
                      ${plan.price}
                    </span>
                    <span className="text-text-secondary font-caption">
                      /{plan.period}
                    </span>
                  </div>
                </div>

                <ul className="space-y-3">
                  {plan.features.map((feature, index) => (
                    <li key={index} className="flex items-start space-x-2">
                      <Icon name="Check" size={16} color="#228B22" className="mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-text-secondary font-caption">
                        {feature}
                      </span>
                    </li>
                  ))}
                </ul>

                <div className="mt-6">
                  <div className={`w-full py-2 px-4 rounded-lg border-2 text-center font-medium transition-smooth ${
                    formData.selectedPlan === plan.id
                      ? 'border-primary bg-primary text-primary-foreground'
                      : 'border-border text-text-secondary hover:border-primary'
                  }`}>
                    {formData.selectedPlan === plan.id ? 'Selected' : 'Select Plan'}
                  </div>
                </div>
              </button>
            </div>
          ))}
        </div>
        
        {errors.selectedPlan && (
          <p className="text-error text-sm font-caption mt-2 flex items-center space-x-1">
            <Icon name="AlertCircle" size={16} color="currentColor" />
            <span>{errors.selectedPlan}</span>
          </p>
        )}
      </div>

      {/* Additional Information */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Additional Information
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Help us understand your business better
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Years in Business
            </label>
            <select
              value={formData.yearsInBusiness || ''}
              onChange={(e) => handleInputChange('yearsInBusiness', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Select years in business</option>
              <option value="0-1">Less than 1 year</option>
              <option value="1-3">1-3 years</option>
              <option value="3-5">3-5 years</option>
              <option value="5-10">5-10 years</option>
              <option value="10+">10+ years</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              Team Size
            </label>
            <select
              value={formData.teamSize || ''}
              onChange={(e) => handleInputChange('teamSize', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Select team size</option>
              <option value="solo">Solo (just me)</option>
              <option value="2-5">2-5 people</option>
              <option value="6-10">6-10 people</option>
              <option value="11-20">11-20 people</option>
              <option value="20+">20+ people</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-text-primary mb-2">
              How did you hear about ShaadiNest?
            </label>
            <select
              value={formData.referralSource || ''}
              onChange={(e) => handleInputChange('referralSource', e.target.value)}
              className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-primary"
            >
              <option value="">Select source</option>
              <option value="google">Google Search</option>
              <option value="social">Social Media</option>
              <option value="referral">Friend/Colleague Referral</option>
              <option value="advertisement">Advertisement</option>
              <option value="event">Wedding Event/Show</option>
              <option value="other">Other</option>
            </select>
          </div>
        </div>
      </div>

      {/* Terms and Conditions */}
      <div>
        <h3 className="font-heading font-semibold text-xl text-text-primary mb-2">
          Terms & Conditions
        </h3>
        <p className="text-text-secondary font-caption mb-6">
          Please review and accept our terms to complete your registration
        </p>

        <div className="space-y-4">
          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.termsAccepted || false}
              onChange={(e) => handleInputChange('termsAccepted', e.target.checked)}
              className={`mt-1 w-4 h-4 text-primary border-border-dark rounded focus:ring-primary focus:ring-2 ${
                errors.termsAccepted ? 'border-error' : ''
              }`}
            />
            <div className="flex-1">
              <p className="text-sm text-text-primary">
                I agree to the{' '}
                <a href="/terms-of-service" className="text-primary hover:text-primary-600 underline">
                  Terms of Service
                </a>{' '}
                and{' '}
                <a href="/terms-of-service" className="text-primary hover:text-primary-600 underline">
                  Vendor Agreement
                </a>
              </p>
              {errors.termsAccepted && (
                <p className="text-error text-sm font-caption mt-1">{errors.termsAccepted}</p>
              )}
            </div>
          </label>

          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.privacyAccepted || false}
              onChange={(e) => handleInputChange('privacyAccepted', e.target.checked)}
              className={`mt-1 w-4 h-4 text-primary border-border-dark rounded focus:ring-primary focus:ring-2 ${
                errors.privacyAccepted ? 'border-error' : ''
              }`}
            />
            <div className="flex-1">
              <p className="text-sm text-text-primary">
                I agree to the{' '}
                <a href="/privacy-policy" className="text-primary hover:text-primary-600 underline">
                  Privacy Policy
                </a>{' '}
                and consent to data processing
              </p>
              {errors.privacyAccepted && (
                <p className="text-error text-sm font-caption mt-1">{errors.privacyAccepted}</p>
              )}
            </div>
          </label>

          <label className="flex items-start space-x-3 cursor-pointer">
            <input
              type="checkbox"
              checked={formData.marketingConsent || false}
              onChange={(e) => handleInputChange('marketingConsent', e.target.checked)}
              className="mt-1 w-4 h-4 text-primary border-border-dark rounded focus:ring-primary focus:ring-2"
            />
            <div className="flex-1">
              <p className="text-sm text-text-primary">
                I would like to receive marketing emails about ShaadiNest updates and tips (optional)
              </p>
            </div>
          </label>
        </div>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-between pt-6 border-t border-border">
        <Button
          variant="outline"
          size="lg"
          onClick={onPrevious}
          iconName="ArrowLeft"
          iconSize={18}
          disabled={isSubmitting}
        >
          Back to Availability
        </Button>
        
        <Button
          variant="primary"
          size="lg"
          onClick={handleSubmit}
          loading={isSubmitting}
          iconName="CheckCircle"
          iconPosition="right"
          iconSize={18}
        >
          {isSubmitting ? 'Submitting...' : 'Complete Registration'}
        </Button>
      </div>
    </div>
  );
};

export default VerificationForm;